import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank'
import CheckBoxOutlinedIcon from '@mui/icons-material/CheckBoxOutlined'
import CloseIcon from '@mui/icons-material/Close'
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutlined'
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined'
import RefreshIcon from '@mui/icons-material/Refresh'
import UploadFileOutlinedIcon from '@mui/icons-material/UploadFileOutlined'
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined'
import { Button, Dialog, DialogActions, DialogContent, DialogTitle } from '@mui/material'
import type { SxProps, Theme } from '@mui/material'
import React from 'react'
import { Link } from 'react-router-dom'
import Drawer from '../../../components/Drawer'
import GrantContent from '../../../components/GrantContent'
import * as Permisos from '../../../utils/Permisos'
import {
    BITACORA_FILTERS,
    chipClass,
    extTone,
    fileExt,
    formatActivityDate,
    isBusy,
    NOTE_TYPES,
    noteMeta,
    RamoMark,
} from '../common/claim-common'
import { useClaimDrawerModel } from './claim-drawer-model'
import type { ClaimDrawerProps } from './claim-drawer-model'

export type { ClaimDrawerProps, ClaimDrawerTab } from './claim-drawer-model'

const actionClass = 'inline-flex h-[38px] items-center gap-1.5 rounded-[10px] border border-k-line-strong bg-transparent px-3 text-[13px] font-bold text-k-ink no-underline hover:bg-k-hover disabled:cursor-not-allowed disabled:opacity-40'

const outlineBtnSx = {
    width: 'auto',
    minWidth: 'auto',
    flex: 'none',
    height: 40,
    padding: '0 16px',
    borderRadius: '12px',
    boxShadow: 'none',
    textTransform: 'none',
    fontWeight: 600,
    fontSize: '14px',
    lineHeight: 1,
    backgroundColor: 'rgb(var(--k-surface))',
    color: 'rgb(var(--k-ink))',
    border: '1px solid rgb(var(--k-line))',
    '&:hover': {
        backgroundColor: 'rgb(var(--k-hover))',
        border: '1px solid rgb(var(--k-line))',
        boxShadow: 'none',
    },
} satisfies SxProps<Theme>

const dangerBtnSx: SxProps<Theme> = {
    ...outlineBtnSx,
    backgroundColor: 'rgb(var(--k-danger))',
    color: '#fff',
    border: 0,
    '&:hover': {
        backgroundColor: 'rgb(var(--k-danger))',
        filter: 'brightness(1.08)',
        boxShadow: 'none',
    },
}

const Field = ({ label, children, wide }: { label: string; children?: React.ReactNode; wide?: boolean }) => (
    <div className={wide ? 'sm:col-span-2' : ''}>
        <dt className="mb-0.5 text-xs text-k-muted">{label}</dt>
        <dd className="m-0 break-words text-sm font-semibold text-k-ink">{children || '—'}</dd>
    </div>
)

const Section = ({ title, children }: { title: string; children: React.ReactNode }) => (
    <section className="flex flex-col gap-3 rounded-2xl border border-k-line bg-k-surface px-[18px] py-4">
        <h3 className="m-0 text-[13px] font-extrabold uppercase tracking-[0.06em] text-k-muted">{title}</h3>
        {children}
    </section>
)

const Pulse = ({ className }: { className: string }) => <div className={`animate-pulse rounded bg-k-line ${className}`} />

const EmptyState = ({ title, hint }: { title: string; hint?: string }) => (
    <div className="flex flex-col items-center justify-center rounded-2xl border border-k-line bg-k-surface px-4 py-10 text-center">
        <p className="m-0 text-sm font-bold text-k-ink">{title}</p>
        {hint && <p className="mb-0 mt-1 text-xs text-k-muted">{hint}</p>}
    </div>
)

const ClaimDrawer = (props: ClaimDrawerProps) => {
    const { siniestroId, onClose, onEdit } = props
    const model = useClaimDrawerModel(props)
    const { siniestro, poliza, cliente, loading, status, resolucion } = model

    return (
        <>
            <Drawer open={Boolean(siniestroId)} onClose={onClose} bare width={680}>
                <div className="flex h-full min-h-0 flex-col" role="dialog" aria-modal="true" aria-label={siniestro?.noSiniestro ? `Detalle del siniestro ${siniestro.noSiniestro}` : 'Detalle del siniestro'}>
                    <div className="flex shrink-0 flex-col gap-4 border-b border-k-line px-6 pt-5">
                        <div className="flex items-start gap-3.5">
                            <RamoMark ramo={model.ramo} boxed large />
                            <div className="flex min-w-0 flex-1 flex-col gap-1.5">
                                <h2 className="m-0 truncate text-[21px] font-extrabold tracking-[-0.02em] text-k-ink">
                                    {siniestro?.noSiniestro ? `Siniestro ${siniestro.noSiniestro}` : 'Detalle del siniestro'}
                                </h2>
                                {siniestro && !loading && status && (
                                    <>
                                        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[13px] text-k-muted">
                                            <span className="tabular-nums">Póliza {poliza?.noPoliza || '—'}</span>
                                            <span>{model.aseguradora}</span>
                                            <span>
                                                Cliente:{' '}
                                                {cliente?.id ? (
                                                    <Link to={`/clientes/${cliente.id}`} className="font-bold text-k-accent-ink no-underline hover:underline">{model.name || '—'}</Link>
                                                ) : (model.name || '—')}
                                            </span>
                                        </div>
                                        <div className="flex flex-wrap gap-1.5">
                                            <span className={`rounded-full px-[9px] py-[3px] text-xs font-bold ${status.className}`}>{status.label}</span>
                                            {model.ramo && <span className="rounded-full bg-k-accent-soft px-[9px] py-[3px] text-xs font-bold text-k-accent-ink">{model.ramo}</span>}
                                        </div>
                                    </>
                                )}
                            </div>
                            <button type="button" aria-label="Cerrar" className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-k-line bg-transparent text-k-ink hover:bg-k-hover" onClick={onClose}>
                                <CloseIcon style={{ fontSize: 18 }} />
                            </button>
                        </div>

                        {siniestro && !loading && (
                            <div className="flex flex-wrap gap-2">
                                <GrantContent permisos={[Permisos.SINIESTROS_W]}>
                                    <button type="button" className={actionClass} onClick={() => onEdit?.(siniestro.id)}>
                                        <EditOutlinedIcon style={{ fontSize: 15 }} />Editar
                                    </button>
                                </GrantContent>
                                <GrantContent permisos={[Permisos.SINIESTROS_D]}>
                                    <button type="button" className={`${actionClass} text-k-danger-ink hover:bg-k-danger-soft`} onClick={model.askDelete} disabled={model.deleting}>
                                        <DeleteOutlinedIcon style={{ fontSize: 15 }} />Eliminar
                                    </button>
                                </GrantContent>
                            </div>
                        )}

                        <div role="tablist" aria-label="Secciones del siniestro" className="-mx-2 flex gap-0.5 overflow-x-auto">
                            {model.tabs.map((item) => {
                                const selected = model.tab === item.id
                                return (
                                    <button
                                        key={item.id}
                                        type="button"
                                        role="tab"
                                        aria-selected={selected}
                                        className={`inline-flex min-h-12 items-center gap-2 whitespace-nowrap border-0 border-b-[3px] bg-transparent px-3 text-sm ${selected ? 'border-k-primary font-extrabold text-k-ink' : 'border-transparent font-semibold text-k-muted'}`}
                                        onClick={() => model.setTab(item.id)}
                                    >
                                        {item.label}
                                        {item.count != null && (
                                            <span className={`inline-flex h-5 min-w-[22px] items-center justify-center rounded-[10px] px-1.5 text-xs font-bold ${selected ? 'bg-k-accent-soft text-k-accent-ink' : 'bg-k-line text-k-ink-2'}`}>
                                                {item.count}
                                            </span>
                                        )}
                                    </button>
                                )
                            })}
                        </div>
                    </div>

                    <div role="tabpanel" className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto bg-k-bg px-6 py-5">
                        {loading && (
                            <div className="flex flex-col items-center justify-center gap-4 py-12">
                                <RefreshIcon className="animate-spin text-k-primary" style={{ fontSize: 40 }} />
                                <p className="text-sm text-k-muted">Cargando detalle...</p>
                            </div>
                        )}

                        {model.error && !loading && (
                            <div className="rounded-lg border border-k-danger/30 bg-k-danger-soft p-4">
                                <div className="flex items-center gap-2">
                                    <ErrorOutlineIcon className="text-k-danger" style={{ fontSize: 20 }} />
                                    <p className="text-sm text-k-danger-ink">{model.error}</p>
                                </div>
                                <Button className="mt-3" variant="contained" onClick={model.reload}>Reintentar</Button>
                            </div>
                        )}

                        {siniestro && !loading && model.tab === 'general' && (
                            <>
                                <div className="grid grid-cols-1 gap-2.5 sm:grid-cols-2">
                                    <div className="flex flex-col gap-1 rounded-[14px] border border-k-line bg-k-surface px-3.5 py-3">
                                        <span className="text-xs font-semibold text-k-muted">Fecha del siniestro</span>
                                        <span className="text-lg font-extrabold tabular-nums">{model.fechaSiniestro}</span>
                                    </div>
                                    <div className="flex flex-col gap-1 rounded-[14px] border border-k-line bg-k-surface px-3.5 py-3">
                                        <span className="text-xs font-semibold text-k-muted">Resolución estimada</span>
                                        <span className="text-lg font-extrabold tabular-nums">{resolucion.label}</span>
                                        <span className={`text-xs font-semibold ${resolucion.noteClass}`}>{resolucion.note || ' '}</span>
                                    </div>
                                </div>

                                <Section title="Descripción">
                                    <p className={`m-0 whitespace-pre-line break-words text-sm ${siniestro.descripcion ? 'text-k-ink-2' : 'italic text-k-muted'}`}>
                                        {siniestro.descripcion || 'Sin descripción'}
                                    </p>
                                </Section>

                                <Section title="Póliza afectada">
                                    <dl className="m-0 grid grid-cols-1 gap-x-[18px] gap-y-3 sm:grid-cols-2">
                                        <Field label="Número">{poliza?.noPoliza}</Field>
                                        <Field label="Ramo">{model.ramo}</Field>
                                        <Field label="Aseguradora">{model.aseguradora}</Field>
                                        <Field label="Clave de agente">{poliza?.agenteClave?.clave}</Field>
                                        <Field label="Concepto" wide>{poliza?.concepto}</Field>
                                        <Field label="Contratante" wide>{model.name}</Field>
                                    </dl>
                                </Section>

                                <Section title="Entidad asegurada">
                                    {model.showAsegurados && (
                                        model.asegurados.length === 0
                                            ? <p className="m-0 text-sm italic text-k-muted">La póliza no tiene asegurados registrados.</p>
                                            : (
                                                <ul className="m-0 flex list-none flex-col gap-2 p-0">
                                                    {model.asegurados.map((item) => (
                                                        <li key={item.id} className={`flex items-center gap-2 text-sm ${item.selected ? 'font-semibold text-k-ink' : 'text-k-muted'}`}>
                                                            {item.selected
                                                                ? <CheckBoxOutlinedIcon className="text-k-primary" style={{ fontSize: 18 }} />
                                                                : <CheckBoxOutlineBlankIcon style={{ fontSize: 18 }} />}
                                                            <span className="min-w-0 truncate">{item.name || '—'}</span>
                                                            {item.parentesco && <span className="text-xs text-k-muted">({item.parentesco})</span>}
                                                        </li>
                                                    ))}
                                                </ul>
                                            )
                                    )}
                                    {model.vehiculo && (
                                        <dl className="m-0 grid grid-cols-1 gap-x-[18px] gap-y-3 sm:grid-cols-2">
                                            <Field label="Vehículo" wide>{model.vehiculoLabel}</Field>
                                            <Field label="Placas">{model.vehiculo.placas}</Field>
                                            <Field label="Número de serie">{model.vehiculo.noSerie}</Field>
                                        </dl>
                                    )}
                                    {model.bienTexto && <p className="m-0 whitespace-pre-line text-sm text-k-ink-2">{model.bienTexto}</p>}
                                    {!model.showAsegurados && !model.vehiculo && !model.bienTexto && (
                                        <p className="m-0 text-sm italic text-k-muted">Sin datos del bien asegurado.</p>
                                    )}
                                </Section>
                            </>
                        )}

                        {siniestro && !loading && model.tab === 'bitacora' && (
                            <>
                                <GrantContent permisos={[Permisos.BITACORA_W]}>
                                    <section className="flex flex-col gap-2.5 rounded-2xl border border-k-line bg-k-surface p-3.5">
                                        <div role="radiogroup" aria-label="Tipo de registro" className="flex flex-wrap gap-1.5">
                                            {NOTE_TYPES.map((item) => (
                                                <button key={item.id} type="button" role="radio" aria-checked={model.noteType === item.id} className={chipClass(model.noteType === item.id)} onClick={() => model.setNoteType(item.id)}>
                                                    <item.Icon style={{ fontSize: 16 }} />
                                                    {item.label}
                                                </button>
                                            ))}
                                        </div>
                                        <label className="flex flex-col">
                                            <span className="sr-only">Nueva nota del siniestro</span>
                                            <textarea rows={3} value={model.note} placeholder={model.notePlaceholder} className="resize-y rounded-xl border border-k-line-strong bg-k-bg px-3 py-2.5 text-sm text-k-ink" onChange={(event) => model.setNote(event.target.value)} />
                                        </label>
                                        <div className="flex justify-end">
                                            <button type="button" disabled={!model.note.trim() || model.savingNote} className={`h-10 rounded-[10px] border-0 px-3.5 text-[13px] font-bold ${model.note.trim() && !model.savingNote ? 'cursor-pointer bg-k-primary text-white' : 'cursor-not-allowed bg-k-line text-k-muted-2'}`} onClick={model.addNote}>
                                                {model.savingNote ? 'Guardando…' : 'Agregar a bitácora'}
                                            </button>
                                        </div>
                                    </section>
                                </GrantContent>
                                <div className="flex flex-wrap gap-1.5" role="group" aria-label="Filtrar bitácora">
                                    {BITACORA_FILTERS.map((item) => (
                                        <button key={item.id} type="button" aria-pressed={model.bitacoraFilter === item.id} className={chipClass(model.bitacoraFilter === item.id)} onClick={() => model.setBitacoraFilter(item.id)}>
                                            {item.label}
                                        </button>
                                    ))}
                                </div>
                                {isBusy(model.bitacoraState) && (
                                    <div className="flex flex-col" aria-busy="true" aria-label="Cargando bitácora">
                                        {[1, 2, 3].map((row) => <Pulse key={row} className="mb-2 h-16 w-full rounded-xl" />)}
                                    </div>
                                )}
                                {model.bitacoraError && <p className="m-0 text-sm text-k-danger-ink">{model.bitacoraError}</p>}
                                {model.bitacoraState === 'ready' && model.visibleNotes.length === 0 && (
                                    <EmptyState title="No hay registros" hint={model.bitacoraFilter === 'todo' ? 'Este siniestro aún no tiene bitácora.' : 'No hay registros en esta categoría.'} />
                                )}
                                {model.bitacoraState === 'ready' && model.visibleNotes.length > 0 && (
                                    <div className="flex flex-col">
                                        {model.visibleNotes.map((item, index) => {
                                            const meta = noteMeta(item.tipo)
                                            return (
                                                <div key={item.id || index} className="flex gap-3.5">
                                                    <div className="flex flex-col items-center">
                                                        <span className={`flex h-[34px] w-[34px] items-center justify-center rounded-[10px] ${meta.tone}`}>
                                                            <meta.Icon style={{ fontSize: 16 }} />
                                                        </span>
                                                        {index < model.visibleNotes.length - 1 && <span className="my-1 w-0.5 flex-1 bg-k-line" />}
                                                    </div>
                                                    <div className="flex min-w-0 flex-1 flex-col gap-1 pb-[18px] pt-1">
                                                        <div className="flex flex-wrap justify-between gap-x-2.5 gap-y-1">
                                                            <span className="text-sm font-extrabold">{meta.label}</span>
                                                            <span className="text-xs tabular-nums text-k-muted">{formatActivityDate(item.fecha) || '—'}</span>
                                                        </div>
                                                        {item.texto && (
                                                            <div
                                                                className="m-0 break-words rounded-xl border border-k-line bg-k-surface px-3 py-2.5 text-sm leading-normal text-k-ink-2 [&_a]:text-k-accent-ink [&_a]:underline [&_img]:max-w-full [&_p]:m-0 [&_p+p]:mt-2"
                                                                dangerouslySetInnerHTML={{ __html: String(item.texto).replace(/\n/g, '<br/>') }}
                                                            />
                                                        )}
                                                    </div>
                                                </div>
                                            )
                                        })}
                                    </div>
                                )}
                            </>
                        )}

                        {siniestro && !loading && model.tab === 'adjuntos' && (
                            <>
                                <GrantContent permisos={[Permisos.ADJUNTOS_W]}>
                                    <label className={`flex cursor-pointer items-center gap-3.5 rounded-2xl border-[1.5px] border-dashed border-k-line-strong bg-k-surface p-4 ${model.uploading ? 'opacity-60' : ''}`}>
                                        <span className="flex h-11 w-11 items-center justify-center rounded-[14px] bg-tone-blue text-tone-blue-ink">
                                            <UploadFileOutlinedIcon />
                                        </span>
                                        <span className="flex flex-col gap-0.5">
                                            <span className="text-sm font-extrabold text-k-ink">{model.uploading ? 'Subiendo archivos…' : 'Subir archivos al siniestro'}</span>
                                            <span className="text-xs text-k-muted">Fotos, dictámenes, facturas · máx. 10 MB</span>
                                        </span>
                                        <input type="file" multiple className="sr-only" disabled={model.uploading} onChange={(event) => { model.uploadFiles(event.target.files); event.target.value = '' }} />
                                    </label>
                                </GrantContent>
                                {isBusy(model.adjuntosState) && (
                                    <div className="flex flex-col gap-2" aria-busy="true">
                                        {[1, 2, 3].map((row) => <Pulse key={row} className="h-16 w-full rounded-[14px]" />)}
                                    </div>
                                )}
                                {model.adjuntosError && <p className="m-0 text-sm text-k-danger-ink">{model.adjuntosError}</p>}
                                {model.adjuntosState === 'ready' && model.adjuntos.length === 0 && <EmptyState title="No hay adjuntos" hint="Este siniestro aún no tiene archivos." />}
                                {model.adjuntosState === 'ready' && model.adjuntos.length > 0 && (
                                    <div className="flex flex-col gap-2">
                                        {model.adjuntos.map((adjunto) => {
                                            const ext = fileExt(adjunto.archivo)
                                            return (
                                                <div key={adjunto.id} className="flex items-center gap-3 rounded-[14px] border border-k-line bg-k-surface px-3.5 py-3">
                                                    <span className={`flex h-11 w-9 items-end justify-center rounded-[7px] pb-[5px] text-[10px] font-extrabold ${extTone(ext)}`}>{ext}</span>
                                                    <span className="flex min-w-0 flex-1 flex-col gap-[3px]">
                                                        <span className="truncate text-sm font-bold">{adjunto.archivo || 'Archivo'}</span>
                                                        <span className="text-xs text-k-muted">{formatActivityDate(adjunto.fecha) || '—'}</span>
                                                    </span>
                                                    <button type="button" aria-label={`Ver ${adjunto.archivo}`} className="inline-flex h-9 w-9 items-center justify-center rounded-[10px] border border-k-line bg-transparent text-k-ink" onClick={() => model.openFile(adjunto.path)}>
                                                        <VisibilityOutlinedIcon style={{ fontSize: 16 }} />
                                                    </button>
                                                    <button type="button" aria-label={`Descargar ${adjunto.archivo}`} className="inline-flex h-9 w-9 items-center justify-center rounded-[10px] border border-k-line bg-transparent text-k-ink" onClick={() => model.openFile(adjunto.path)}>
                                                        <FileDownloadOutlinedIcon style={{ fontSize: 16 }} />
                                                    </button>
                                                    <GrantContent permisos={[Permisos.ADJUNTOS_D]}>
                                                        {model.confirmDeleteId === adjunto.id ? (
                                                            <button type="button" className="h-9 rounded-[10px] border border-k-danger px-2 text-xs font-bold text-k-danger" onClick={() => model.deleteAdjunto(adjunto.id)}>Eliminar</button>
                                                        ) : (
                                                            <button type="button" aria-label={`Eliminar ${adjunto.archivo}`} className="inline-flex h-9 w-9 items-center justify-center rounded-[10px] border border-k-line bg-transparent text-k-danger" onClick={() => model.setConfirmDeleteId(adjunto.id)}>
                                                                <DeleteOutlinedIcon style={{ fontSize: 16 }} />
                                                            </button>
                                                        )}
                                                    </GrantContent>
                                                </div>
                                            )
                                        })}
                                    </div>
                                )}
                            </>
                        )}
                    </div>

                    <div className="flex shrink-0 flex-wrap items-center justify-end gap-2.5 border-t border-k-line bg-k-surface px-6 py-3.5">
                        <button type="button" className="h-11 rounded-xl border border-k-line-strong bg-transparent px-4 text-sm font-bold text-k-ink" onClick={onClose}>Cerrar</button>
                    </div>
                </div>
            </Drawer>

            <Dialog open={model.confirmDelete} onClose={model.cancelDelete} slotProps={{ paper: { className: 'k-dialog' } }}>
                <DialogTitle>Eliminar siniestro</DialogTitle>
                <DialogContent>
                    <p className="m-0 text-sm text-k-ink-2">¿Desea eliminar el siniestro? Si continúa, se perderá toda la información relacionada con la bitácora y los adjuntos.</p>
                </DialogContent>
                <DialogActions>
                    <Button variant="outlined" sx={outlineBtnSx} onClick={model.cancelDelete}>Cancelar</Button>
                    <Button variant="contained" sx={dangerBtnSx} onClick={model.deleteSiniestro} disabled={model.deleting}>Eliminar</Button>
                </DialogActions>
            </Dialog>

            {model.toast && (
                <div role="status" className="fixed bottom-6 left-6 z-[60] flex items-center gap-2.5 rounded-xl bg-[#1B1D29] px-4 py-3 text-sm text-white shadow-[0_12px_32px_rgba(10,12,20,0.3)]">
                    {model.toast}
                </div>
            )}
        </>
    )
}

export default ClaimDrawer
